import AsyncStorage from '@react-native-async-storage/async-storage';

/** AsyncStorage keys. (AsyncStorage maps to localStorage on web.) */
export const KEYS = {
  token: 'hrms.token',
  serverUrl: 'hrms.serverUrl',
  themeMode: 'hrms.themeMode',
  lastTenant: 'hrms.lastTenant',
  lastEmail: 'hrms.lastEmail',
} as const;

/**
 * Tiny async key-value wrapper with an in-memory cache so hot paths (the API token) can be read
 * synchronously after `hydrateStorage()` has run once at startup.
 */
const cache = new Map<string, string | null>();

export async function hydrateStorage() {
  const keys = Object.values(KEYS);
  try {
    const pairs = await AsyncStorage.multiGet(keys);
    pairs.forEach(([k, v]) => cache.set(k, v));
  } catch {
    keys.forEach((k) => cache.set(k, null));
  }
}

export const storage = {
  peek: (key: string): string | null => cache.get(key) ?? null,
  async get(key: string): Promise<string | null> {
    if (cache.has(key)) return cache.get(key) ?? null;
    const v = await AsyncStorage.getItem(key).catch(() => null);
    cache.set(key, v);
    return v;
  },
  async set(key: string, value: string | null) {
    cache.set(key, value);
    try {
      if (value === null) await AsyncStorage.removeItem(key);
      else await AsyncStorage.setItem(key, value);
    } catch {
      /* storage unavailable (private mode) — keep the in-memory value */
    }
  },
};

export const getToken = () => storage.peek(KEYS.token);
export const setToken = (t: string | null) => storage.set(KEYS.token, t);
